export type ServiceTableCategory =
  | 'hoteles'
  | 'guias'
  | 'transportes'
  | 'operadores'
  | 'tickets'
  | 'alimentos'
  | 'personalCampo'
  | 'coordinadores'
  | 'otros';

export interface ServiceTableConfig {
  sectionKey: string;
  label: string;
  dataKey: string;
  columns: string[];
  serviceTypeForArchivos?: number | string;
  highlightInvalidFecha?: boolean;
  supportsMainGuide?: boolean;
  supportsExpandRows?: boolean;
  useLastDaySsffChip?: boolean;
  createOrderContextLabel?: string;
  /** Solo Alimentos: createPurchaseNote en lugar de createOrder. */
  usesPurchaseNote?: boolean;
  createPurchaseNoteContextLabel?: string;
}

const STANDARD_COLUMNS = [
  'Check',
  'ssff',
  'serviceDay',
  'fecha',
  'nombreServicio',
  'nombre',
  'moneda',
  'pax',
  'tc',
  'costoUnitario',
  'costo',
  'orderNumber',
  'orderStatus',
  'freezeDeadlineAt',
  'scheduledPaymentDate',
  'paidAt',
  'reservationNumber',
  'reservationStatus',
  'correoReserva',
  'observation',
  'orderObservation',
  'verPDF',
  'archivos'
] as const;

/** Cabeceras UPPERCASE compartidas (Programación de servicios / OS Masivas). */
export const SERVICE_TABLE_COLUMN_HEADERS = {
  serviceDay: 'DÍA',
  fecha: 'FECHA',
  tipoServicio: 'TIPO',
  categoriaServicio: 'CATEGORÍA',
  nombreServicio: 'ACTIVIDAD',
  nombre: 'PROVEEDOR',
  moneda: 'MONEDA',
  pax: 'PAX',
  tc: 'TC',
  costoUnitario: 'COSTO UNITARIO',
  costo: 'MONTO',
  orderNumber: 'N° OS',
  orderStatus: 'ESTADO',
  freezeDeadlineAt: 'CORTE',
  purchaseNoteNumber: 'N° OP',
  purchaseNoteStatus: 'ESTADO OP',
  scheduledPaymentDate: 'PAGO PROGRAMADO',
  purchaseNoteReviewDate: 'FECHA REVISIÓN',
  paidAt: 'PAGADO',
  reservationNumber: 'N° RESERVA',
  reservationStatus: 'ESTADO RESERVA',
  correoReserva: 'CORREO',
  observation: 'OBSERVACIÓN',
  orderObservation: 'OBSERVACIÓN OS',
  purchaseNoteObservation: 'OBSERVACIÓN OP',
  verPDF: 'ACCIONES',
  verNP: 'ACCIONES',
  archivos: 'ARCHIVOS',
  tipoTarifaAlojamiento: 'TARIFA ALOJAMIENTO',
  tipoDeUnidad: 'TIPO DE UNIDAD',
  mainGuide: 'GUÍA PRINCIPAL',
  estadoServicioTickets: 'ESTADO SERVICIO'
} as const;

/**
 * Padre TICKET (resumen tipo Ticketería).
 * OS / reserva / PDF viven en el expand por ticket (un padre puede tener N tickets/OS).
 */
export const TICKET_SERVICE_SUMMARY_COLUMNS: string[] = [
  'Check',
  'ssff',
  'serviceDay',
  'fecha',
  'categoriaServicio',
  'nombreServicio',
  'nombre',
  'moneda',
  'pax',
  'tc',
  'costo',
  'estadoServicioTickets',
  'observation'
];

/**
 * Columnas OS / OP Masivas.
 * Bloques: contexto servicio → OS → OP → acciones.
 */
export const MASS_OS_SERVICE_COLUMNS: string[] = [
  'Check',
  'serviceDay',
  'fecha',
  'tipoServicio',
  'nombreServicio',
  'nombre',
  'moneda',
  'pax',
  'costo',
  'orderNumber',
  'orderStatus',
  'freezeDeadlineAt',
  'scheduledPaymentDate',
  'paidAt',
  'purchaseNoteNumber',
  'purchaseNoteStatus',
  'purchaseNoteReviewDate',
  'verPDF'
];

/**
 * Columnas OS Programación Aventura (personal de campo).
 * Alineadas a STANDARD_COLUMNS (cabeceras UPPERCASE compartidas).
 */
export const AVENTURA_OS_SERVICE_COLUMNS: string[] = [
  'Check',
  'serviceDay',
  'fecha',
  'categoriaServicio',
  'nombreServicio',
  'nombre',
  'moneda',
  'pax',
  'costo',
  'orderNumber',
  'orderStatus',
  'freezeDeadlineAt',
  'scheduledPaymentDate',
  'reservationNumber',
  'reservationStatus',
  'verPDF'
];

function withColumns(
  base: readonly string[],
  insertAfter: string,
  extra: string[]
): string[] {
  const index = base.indexOf(insertAfter);
  if (index === -1) {
    return [...base, ...extra];
  }
  return [...base.slice(0, index + 1), ...extra, ...base.slice(index + 1)];
}

/**
 * Alimentos y bebidas: OS + Orden de Pedido en la misma grilla.
 * Conserva columnas OS estándar y agrega las de OP.
 */
export const ALIMENTOS_PURCHASE_NOTE_COLUMNS: string[] = [
  'Check',
  'ssff',
  'serviceDay',
  'fecha',
  'nombreServicio',
  'nombre',
  'moneda',
  'pax',
  'tc',
  'costoUnitario',
  'costo',
  'orderNumber',
  'orderStatus',
  'freezeDeadlineAt',
  'scheduledPaymentDate',
  'paidAt',
  'reservationNumber',
  'reservationStatus',
  'purchaseNoteNumber',
  'purchaseNoteStatus',
  'purchaseNoteReviewDate',
  'observation',
  'orderObservation',
  'purchaseNoteObservation',
  'verPDF'
];

function withoutColumns(base: readonly string[], remove: string[]): string[] {
  return base.filter((column) => !remove.includes(column));
}

export const SERVICE_TABLE_CONFIGS: Record<
  ServiceTableCategory,
  ServiceTableConfig
> = {
  hoteles: {
    sectionKey: 'hoteles',
    label: 'Hoteles',
    dataKey: 'alojamiento',
    columns: withColumns(STANDARD_COLUMNS, 'nombreServicio', [
      'tipoTarifaAlojamiento'
    ]),
    serviceTypeForArchivos: 5,
    useLastDaySsffChip: true
  },
  guias: {
    sectionKey: 'guias',
    label: 'Guías',
    dataKey: 'guia',
    columns: [
      ...withoutColumns(STANDARD_COLUMNS, ['archivos']),
      'mainGuide',
      'archivos'
    ],
    serviceTypeForArchivos: 4,
    supportsMainGuide: true
  },
  transportes: {
    sectionKey: 'transportes',
    label: 'Transportes',
    dataKey: 'transporte',
    columns: withColumns(STANDARD_COLUMNS, 'costoUnitario', ['tipoDeUnidad']),
    serviceTypeForArchivos: 2
  },
  operadores: {
    sectionKey: 'operadores',
    label: 'Operadores',
    dataKey: 'operator',
    columns: [...STANDARD_COLUMNS],
    serviceTypeForArchivos: 5
  },
  tickets: {
    sectionKey: 'tickets',
    label: 'Tickets',
    dataKey: 'tickets',
    columns: [...TICKET_SERVICE_SUMMARY_COLUMNS],
    serviceTypeForArchivos: 'TICKET',
    highlightInvalidFecha: true,
    supportsExpandRows: true,
    createOrderContextLabel: 'Tickets'
  },
  alimentos: {
    sectionKey: 'alimentos',
    label: 'Alimentos',
    dataKey: 'ayb',
    columns: [...ALIMENTOS_PURCHASE_NOTE_COLUMNS],
    supportsExpandRows: true,
    usesPurchaseNote: true,
    createOrderContextLabel: 'Alimentos y bebidas',
    createPurchaseNoteContextLabel: 'Alimentos y bebidas'
  },
  personalCampo: {
    sectionKey: 'personalCampo',
    label: 'Personal de campo',
    dataKey: 'personalCampo',
    columns: withoutColumns(
      withColumns(STANDARD_COLUMNS, 'nombreServicio', ['categoriaServicio']),
      ['moneda', 'archivos']
    )
  },
  coordinadores: {
    sectionKey: 'coordinadores',
    label: 'Coordinadores',
    dataKey: 'coordinadores',
    columns: [...STANDARD_COLUMNS],
    serviceTypeForArchivos: 6
  },
  otros: {
    sectionKey: 'otros',
    label: 'Otros',
    dataKey: 'otros',
    columns: withoutColumns(STANDARD_COLUMNS, ['archivos'])
  }
};

export const SERVICE_TABLE_TABS: Array<{
  id: string;
  label: string;
  kind: 'all' | 'table';
  category?: ServiceTableCategory;
}> = [
  { id: 'services', label: 'All Services', kind: 'all' },
  { id: 'hoteles', label: 'Hoteles', kind: 'table', category: 'hoteles' },
  { id: 'guias', label: 'Guías', kind: 'table', category: 'guias' },
  {
    id: 'transportes',
    label: 'Transportes',
    kind: 'table',
    category: 'transportes'
  },
  {
    id: 'operadores',
    label: 'Operadores',
    kind: 'table',
    category: 'operadores'
  },
  { id: 'tickets', label: 'Tickets', kind: 'table', category: 'tickets' },
  { id: 'alimentos', label: 'Alimentos', kind: 'table', category: 'alimentos' },
  {
    id: 'personalCampo',
    label: 'Personal de campo',
    kind: 'table',
    category: 'personalCampo'
  },
  {
    id: 'coordinadores',
    label: 'Coordinadores',
    kind: 'table',
    category: 'coordinadores'
  },
  { id: 'otros', label: 'Otros', kind: 'table', category: 'otros' }
];
